import React from 'react';
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Stack,
  styled,
  Fade,
  Backdrop,
  Drawer,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close, AccessTime } from '@mui/icons-material';
import { SlateResponse } from '../common/store/types';
import { siteLogos, sportLogos } from '../utils/logos';

// Styled Components
const ModalContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 500,
  maxHeight: '80vh',
  overflow: 'auto',
  backgroundColor: '#0A1628',
  borderRadius: 16,
  outline: 'none',
  border: '1px solid rgba(1, 90, 206, 0.3)',
  boxShadow: '0 24px 48px rgba(0, 0, 0, 0.5)',
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 10, 22, 0.4)',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(1, 90, 206, 0.4)',
    borderRadius: 3,
  },
  [theme.breakpoints.down('sm')]: {
    width: '95%',
  },
}));

const ModalHeader = styled(Box)({
  background: 'linear-gradient(135deg, #015ACE 0%, #0142A0 100%)',
  padding: '20px 24px',
  position: 'relative',
});

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: 12,
  right: 12,
  color: 'rgba(255, 255, 255, 0.8)',
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    color: '#FFFFFF',
  },
});

const ModalTitle = styled(Typography)({
  fontFamily: "'Oswald', sans-serif",
  fontSize: 24,
  fontWeight: 600,
  color: '#FFFFFF',
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
});

const TimeDisplay = styled(Typography)({
  fontFamily: "'Work Sans', sans-serif",
  fontSize: 14,
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.8)',
  marginTop: 4,
});

const ModalContent = styled(Box)({
  padding: 16,
});

const SlateListItem = styled(Box)({
  backgroundColor: 'rgba(1, 90, 206, 0.1)',
  border: '1px solid rgba(1, 90, 206, 0.2)',
  borderRadius: 8,
  padding: '16px',
  marginBottom: 12,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateX(4px)',
    backgroundColor: 'rgba(1, 90, 206, 0.15)',
    borderColor: 'rgba(1, 90, 206, 0.4)',
  },
  '&:last-child': {
    marginBottom: 0,
  },
});

const SlateName = styled(Typography)({
  fontFamily: "'Work Sans', sans-serif",
  fontSize: 15,
  fontWeight: 600,
  color: '#FFFFFF',
  marginBottom: 8,
});

const SlateMetaRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
});

const LogoBox = styled(Box)({
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
});

const MetaBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 8px',
  borderRadius: 4,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  fontSize: 11,
  fontWeight: 600,
  fontFamily: "'Work Sans', sans-serif",
  color: '#FFFFFF',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
});

// Bottom sheet styles for mobile
const BottomSheetContent = styled(Box)({
  backgroundColor: '#0A1628',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  maxHeight: '80vh',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 10, 22, 0.4)',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(1, 90, 206, 0.4)',
    borderRadius: 3,
  },
});

const BottomSheetHandle = styled(Box)({
  width: 40,
  height: 4,
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  borderRadius: 2,
  margin: '12px auto 0',
});

const BottomSheetHeader = styled(Box)({
  padding: '16px 16px 12px',
  borderBottom: '1px solid rgba(1, 90, 206, 0.2)',
});

const BottomSheetTitle = styled(Typography)({
  fontFamily: "'Oswald', sans-serif",
  fontSize: 18,
  fontWeight: 600,
  color: '#FFFFFF',
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
});

const BottomSheetSubtitle = styled(Typography)({
  fontFamily: "'Work Sans', sans-serif",
  fontSize: 12,
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.6)',
  marginTop: 4,
});

interface MultipleSlatesModalProps {
  open: boolean;
  onClose: () => void;
  slates: SlateResponse[];
  hour: number;
  onSlateSelect: (slate: SlateResponse) => void;
}

const MultipleSlatesModal: React.FC<MultipleSlatesModalProps> = ({
  open,
  onClose,
  slates,
  hour,
  onSlateSelect,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatHour = (h: number) => {
    const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    const amPm = h >= 12 ? 'PM' : 'AM';
    return `${displayHour}:00 ${amPm}`;
  };

  const formatTime = (startTime: string) => {
    const date = new Date(startTime);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Shared slate list content
  const slateListContent = slates.map((slate) => {
    const siteLogo = siteLogos[slate.site?.toLowerCase()];
    const sportLogo = sportLogos[slate.sport?.toUpperCase()];

    return (
      <SlateListItem
        key={slate.slateId}
        onClick={() => {
          onSlateSelect(slate);
          onClose();
        }}
      >
        <SlateName>{slate.name || slate.type || 'Slate'}</SlateName>

        <SlateMetaRow>
          {siteLogo?.logo && (
            <LogoBox>
              <img src={siteLogo.logo} alt={slate.site} />
            </LogoBox>
          )}
          {sportLogo?.logo && (
            <LogoBox>
              <img src={sportLogo.logo} alt={slate.sport} />
            </LogoBox>
          )}
          {slate.type && (
            <MetaBadge>{slate.type}</MetaBadge>
          )}
          <MetaBadge>
            <AccessTime sx={{ fontSize: 10 }} />
            {formatTime(slate.startTime)}
          </MetaBadge>
        </SlateMetaRow>
      </SlateListItem>
    );
  });

  // Mobile: Bottom sheet
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <BottomSheetContent>
          <BottomSheetHandle />
          <BottomSheetHeader>
            <BottomSheetTitle>Multiple Slates</BottomSheetTitle>
            <BottomSheetSubtitle>
              {formatHour(hour)} - {slates.length} slates available
            </BottomSheetSubtitle>
          </BottomSheetHeader>
          <ModalContent>
            {slateListContent}
          </ModalContent>
        </BottomSheetContent>
      </Drawer>
    );
  }

  // Desktop: Centered modal
  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
        sx: {
          backgroundColor: 'rgba(0, 10, 22, 0.85)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <Fade in={open}>
        <ModalContainer>
          <ModalHeader>
            <CloseButton onClick={onClose} size="small">
              <Close fontSize="small" />
            </CloseButton>

            <ModalTitle>Multiple Slates Starting</ModalTitle>
            <TimeDisplay>
              <AccessTime sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
              {formatHour(hour)} - {slates.length} slates available
            </TimeDisplay>
          </ModalHeader>

          <ModalContent>
            {slateListContent}
          </ModalContent>
        </ModalContainer>
      </Fade>
    </Modal>
  );
};

export default MultipleSlatesModal;
